import { useState, useEffect, useRef } from 'react'
import {
  CloseButton,
  Input,
  Button,
  VStack,
  HStack,
  Alert,
  Box,
  Text,
  Badge,
  Field,
  Dialog,
  Portal,
} from '@chakra-ui/react';
import { toaster } from '@/components/ui/toaster'
import { FiUpload, FiTrash2 } from 'react-icons/fi'
import { useProfile, useUpdateProfile, useUploadProfilePortrait, useDeleteProfilePortrait } from '../../hooks/useProfile'
import { UserAvatar } from '../admin/UserAvatar'

interface ProfileEditModalProps {
  isOpen: boolean
  onClose: () => void
}

export function ProfileEditModal({ isOpen, onClose }: ProfileEditModalProps) {
  const { data: profile } = useProfile()
  const updateProfile = useUpdateProfile()
  const uploadPortrait = useUploadProfilePortrait()
  const deletePortrait = useDeleteProfilePortrait()
  const fileInputRef = useRef<HTMLInputElement>(null)

  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')

  useEffect(() => {
    if (profile) {
      setFirstName(profile.firstName)
      setLastName(profile.lastName)
      setEmail(profile.email)
      setPhone(profile.phone || '')
    }
  }, [profile])

  const handleSubmit = async () => {
    if (!profile) return

    try {
      await updateProfile.mutateAsync({
        firstName,
        lastName,
        email,
        phone: phone || undefined,
      })
      toaster.create({
        title: 'Profile updated',
        type: 'success',
        duration: 3000,
      })
      onClose()
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Failed to update profile'
      toaster.create({
        title: 'Error',
        description: message,
        type: 'error',
        duration: 5000,
      })
    }
  }

  const handlePortraitChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    try {
      await uploadPortrait.mutateAsync(file)
      toaster.create({ title: 'Portrait uploaded', type: 'success', duration: 2000 })
    } catch {
      toaster.create({ title: 'Failed to upload portrait', type: 'error', duration: 4000 })
    }
    e.target.value = ''
  }

  const handleDeletePortrait = async () => {
    try {
      await deletePortrait.mutateAsync()
      toaster.create({ title: 'Portrait removed', type: 'success', duration: 2000 })
    } catch {
      toaster.create({ title: 'Failed to remove portrait', type: 'error', duration: 4000 })
    }
  }

  return (
    <Dialog.Root open={isOpen} size='md' onOpenChange={e => {
      if (!e.open) {
        onClose();
      }
    }}>
      <Portal>

        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content>
            <Dialog.Header>Edit Profile</Dialog.Header>
            <Dialog.CloseTrigger asChild><CloseButton size="sm" /></Dialog.CloseTrigger>
            <Dialog.Body>
              <VStack gap={4}>
                {!!updateProfile.error && (
                  <Alert.Root status="error" borderRadius="md">
                    <Alert.Indicator />
                    Failed to update profile. Please try again.
                  </Alert.Root>
                )}

                {/* Portrait */}
                {profile && (
                  <Field.Root>
                    <Field.Label fontSize="sm" color="gray.600">Portrait</Field.Label>
                    <HStack gap={4}>
                      <UserAvatar
                        userId={profile.id}
                        firstName={firstName || profile.firstName}
                        portraitUrl={profile.portraitUrl}
                        size="lg"
                      />
                      <VStack align="start" gap={2}>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => fileInputRef.current?.click()}
                          loading={uploadPortrait.isPending}><FiUpload />{profile.portraitUrl ? 'Replace' : 'Upload'}</Button>
                        {profile.portraitUrl && (
                          <Button
                            size="sm"
                            variant="ghost"
                            colorPalette="red"
                            onClick={handleDeletePortrait}
                            loading={deletePortrait.isPending}><FiTrash2 />Remove
                                                  </Button>
                        )}
                        <Text fontSize="xs" color="gray.400">
                          JPEG, PNG, WebP · max 5 MB
                        </Text>
                      </VStack>
                      <Box display="none">
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept="image/jpeg,image/png,image/webp,image/gif"
                          onChange={handlePortraitChange}
                        />
                      </Box>
                    </HStack>
                  </Field.Root>
                )}

                <HStack gap={4} w="full">
                  <Field.Root>
                    <Field.Label fontSize="sm" color="gray.600">First Name</Field.Label>
                    <Input
                      size="sm"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                    />
                  </Field.Root>
                  <Field.Root>
                    <Field.Label fontSize="sm" color="gray.600">Last Name</Field.Label>
                    <Input
                      size="sm"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                    />
                  </Field.Root>
                </HStack>

                <Field.Root>
                  <Field.Label fontSize="sm" color="gray.600">Email</Field.Label>
                  <Input
                    size="sm"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </Field.Root>

                <Field.Root>
                  <Field.Label fontSize="sm" color="gray.600">Phone</Field.Label>
                  <Input
                    size="sm"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                  />
                </Field.Root>

                {/* Read-only fields */}
                <HStack gap={4} w="full">
                  <Field.Root>
                    <Field.Label fontSize="sm" color="gray.600">Role</Field.Label>
                    <Badge bg="info.subtle" color="info.fg" fontSize="sm" px={2} py={1}>
                      {profile?.role}
                    </Badge>
                  </Field.Root>
                  <Field.Root>
                    <Field.Label fontSize="sm" color="gray.600">Committee</Field.Label>
                    <Text fontSize="sm" color="gray.700">
                      {profile?.committeeLabel || 'Unassigned'}
                    </Text>
                  </Field.Root>
                </HStack>
              </VStack>
            </Dialog.Body>

            <Dialog.Footer>
              <HStack gap={3}>
                <Button variant="outline" size="sm" onClick={onClose}>
                  Cancel
                </Button>
                <Button
                  colorPalette="brand"
                  size="sm"
                  onClick={handleSubmit}
                  loading={updateProfile.isPending}
                >
                  Save Changes
                </Button>
              </HStack>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>

      </Portal>
    </Dialog.Root>
  );
}
